import React from "react";
import { hm, QueueEntryApi } from "../../services/queue/types";
import { patientDisplayName } from "../../services/queue/stats";
import VisitReasonBadge from "../VisitReason/VisitReasonBadge";
import { AppointmentPill, PatientAvatar, WaitTimeBadge } from "./QueueBits";

export type SecretaryEntryActions = {
  /** Rang FIFO dans la file complète, indépendant des filtres. */
  rankById: Map<number, number>;
  onEdit: (entry: QueueEntryApi) => void;
  onRemove: (entry: QueueEntryApi) => void;
};

/** Boutons « Modifier » et « Retirer » d'une entrée. */
export const EntryActions: React.FC<{ entry: QueueEntryApi } & Omit<SecretaryEntryActions, "rankById">> = ({
  entry,
  onEdit,
  onRemove,
}) => (
  <div className="flex items-center justify-end gap-1 opacity-70 transition-opacity group-hover/row:opacity-100">
    <button
      type="button"
      onClick={() => onEdit(entry)}
      title="Modifier le motif ou le médecin"
      aria-label={`Modifier l'entrée de ${patientDisplayName(entry)}`}
      className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-600 transition-colors hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-200"
    >
      <i className="bx bx-edit-alt text-lg" />
    </button>
    <button
      type="button"
      onClick={() => onRemove(entry)}
      title="Retirer de la liste"
      aria-label={`Retirer ${patientDisplayName(entry)} de la liste`}
      className="flex h-9 w-9 items-center justify-center rounded-lg text-rose-600 transition-colors hover:bg-rose-50 focus:outline-none focus:ring-2 focus:ring-rose-200"
    >
      <i className="bx bx-user-x text-lg" />
    </button>
  </div>
);

const HEADER_CELL = "px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500";

/** Liste du jour en ordre d'arrivée, au format tableau du design system. */
const SecretaryQueueTable: React.FC<{ entries: QueueEntryApi[] } & SecretaryEntryActions> = ({
  entries,
  rankById,
  onEdit,
  onRemove,
}) => (
  <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
    <table className="w-full min-w-[900px] text-sm">
      <thead className="bg-slate-50/70">
        <tr>
          <th scope="col" className={`${HEADER_CELL} w-16`}>Rang</th>
          <th scope="col" className={HEADER_CELL}>Patient</th>
          <th scope="col" className={HEADER_CELL}>Motif</th>
          <th scope="col" className={HEADER_CELL}>Médecin</th>
          <th scope="col" className={HEADER_CELL}>Arrivée</th>
          <th scope="col" className={HEADER_CELL}>Attente</th>
          <th scope="col" className={`${HEADER_CELL} text-right`}>Actions</th>
        </tr>
      </thead>
      <tbody>
        {entries.length === 0 && (
          <tr>
            <td colSpan={7} className="px-5 py-10 text-center text-slate-400">
              Aucun patient ne correspond aux filtres.
            </td>
          </tr>
        )}
        {entries.map((entry) => (
          <tr key={entry.id} className="group/row border-t border-slate-100 transition-colors hover:bg-emerald-50/40">
            <td className="px-5 py-3.5 font-bold text-slate-400 tabular-nums">{rankById.get(entry.id)}</td>
            <td className="px-5 py-3.5">
              <div className="flex items-center gap-3">
                <PatientAvatar entry={entry} />
                <div className="min-w-0">
                  <p className="truncate font-semibold text-slate-900">{patientDisplayName(entry)}</p>
                  <div className="mt-1">
                    <AppointmentPill entry={entry} />
                  </div>
                </div>
              </div>
            </td>
            <td className="px-5 py-3.5">
              <VisitReasonBadge label={entry.visitReasonLabel} color={entry.visitReasonColor} />
              {entry.reasonNote && <p className="mt-1 max-w-[240px] truncate text-xs text-slate-500">{entry.reasonNote}</p>}
            </td>
            <td className="px-5 py-3.5 text-slate-700">{entry.assignedDoctorName}</td>
            <td className="px-5 py-3.5 text-slate-500 tabular-nums">{hm(entry.arrivalTime)}</td>
            <td className="px-5 py-3.5">
              <WaitTimeBadge minutes={entry.waitingMinutes} />
            </td>
            <td className="px-5 py-3.5">
              <EntryActions entry={entry} onEdit={onEdit} onRemove={onRemove} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default SecretaryQueueTable;
